import React, { useEffect, useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveImageUrl } from "../../lib/api";

export default function ImageUploader({ value, onChange, label = "Cover Image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [mode, setMode] = useState("upload"); // "upload" | "url"
  const [urlDraft, setUrlDraft] = useState(typeof value === "string" && /^https?:\/\//.test(value) ? value : "");

  useEffect(() => {
    setPreviewError(false);
  }, [value]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Prefer relative path so it survives env URL changes
      onChange(data.path || data.url);
      toast.success("Image uploaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer?.files);
  };

  const clear = () => {
    onChange("");
    setUrlDraft("");
  };

  const preview = resolveImageUrl(value);

  return (
    <div data-testid="image-uploader">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-[#A1A1AA]">{label}</label>
        <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-0.5">
          <button
            type="button"
            onClick={() => setMode("upload")}
            data-testid="img-mode-upload"
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-widest rounded-full ${
              mode === "upload" ? "bg-[#FF8C00] text-[#0A0A0C]" : "text-[#A1A1AA]"
            }`}
          >
            Upload
          </button>
          <button
            type="button"
            onClick={() => setMode("url")}
            data-testid="img-mode-url"
            className={`px-3 py-1 text-[10px] font-mono-tech uppercase tracking-widest rounded-full ${
              mode === "url" ? "bg-[#FF8C00] text-[#0A0A0C]" : "text-[#A1A1AA]"
            }`}
          >
            URL
          </button>
        </div>
      </div>

      {preview && !previewError ? (
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#0A0A0C]">
          <img
            src={preview}
            alt="cover preview"
            className="w-full max-h-72 object-cover"
            onError={() => setPreviewError(true)}
          />
          <button
            type="button"
            onClick={clear}
            data-testid="img-clear"
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-black/60 backdrop-blur text-[10px] font-mono-tech uppercase tracking-widest text-white hover:bg-black/80"
          >
            <X className="w-3 h-3" /> Remove
          </button>
        </div>
      ) : mode === "upload" ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          data-testid="img-dropzone"
          className="relative rounded-xl border border-dashed border-white/15 bg-white/3 hover:border-[#FF8C00]/50 hover:bg-[#FF8C00]/5 transition-colors p-10 text-center cursor-pointer"
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-[#A1A1AA]">
              <Loader2 className="w-5 h-5 animate-spin" /> Uploading…
            </div>
          ) : (
            <>
              <div className="mx-auto w-12 h-12 rounded-xl bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center">
                <Upload className="w-5 h-5 text-[#FF8C00]" />
              </div>
              <p className="mt-3 text-sm font-bold text-white">Drop an image or click to browse</p>
              <p className="text-xs text-[#A1A1AA] mt-1">JPG, PNG, WEBP, GIF — up to 5 MB</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="img-file-input"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      ) : (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6a70]" />
            <input
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://images.unsplash.com/..."
              data-testid="img-url-input"
              className="w-full pl-9 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-[#6a6a70] focus:outline-none focus:border-[#FF8C00] text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => onChange(urlDraft.trim())}
            data-testid="img-url-apply"
            className="px-4 py-3 rounded-lg bg-[#FF8C00] text-[#0A0A0C] text-xs font-black uppercase tracking-widest hover:bg-[#FFA333]"
          >
            Apply
          </button>
        </div>
      )}

      {previewError && preview && (
        <p className="mt-2 text-xs text-red-400 flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> Image failed to load. Try a different URL or re-upload.
        </p>
      )}
    </div>
  );
}
